import {
	HttpClient,
	HttpErrorResponse,
	HttpParams,
} from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import {
	EditCompanyRequestBody,
	UpdateCompanyRelationshipDetailsRequestBody,
} from 'src/app/models/api/requests';
import { Company } from 'src/app/models/data/company';
import {
	Device,
	DeviceAccessSource,
	DeviceGroup,
	DeviceGroupDeviceStatus,
	DeviceInstallationPhoto,
	DeviceNote,
	DeviceNoteResponse,
	DeviceSettings,
	SerialNumber,
} from 'src/app/models/data/device';
import {
	AssignableRelationshipType,
	ControlAssignment,
	ControlAssignmentOption,
	DeviceControl,
} from 'src/app/models/data/deviceControl';
import {
	CompanyEntityRelationship,
	CreateCompanyEntityRelationshipRequestBody,
	CreateCompanyRequestBody,
	EntityRelationship,
} from 'src/app/models/data/entityRelationship';
import {
	OneDayCodeGen3,
	OneDayCodeListResponse,
	OneDayCodeResponse,
} from 'src/app/models/data/oneDayCode';
import {
	FlexCodeGen4,
	FlexCodeListResponse,
} from 'src/app/models/data/flexCode';
import { AccessCodeListResponse } from 'src/app/models/data/accessCode';
import { Role, RoleDataItem } from 'src/app/models/data/role';
import {
	SystemCapability,
	SystemCapabilityItem,
} from 'src/app/models/data/systemCapability';
import {
	MfaMethod,
	PhoneNumber,
	SaveNoteRequestBody,
	SaveStatusRequestBody,
	UpdateUserProfile,
	User,
	UserSecurityAnswer,
	UserWithOptionalErrorMessage,
	UserWithRoleName,
} from 'src/app/models/data/user';
import {
	AccessReportExportStart,
	AccessReportExportStatus,
	AccessReportRecord,
	KeyDetectionReportRecord,
	CodeGenReport,
	InventoryReportRecord,
	PropertyReportRecord,
	CreatePropertyRequestBody,
	DashboardSettings,
	EditPropertyRequestBody,
	EditDeviceRequestBody,
	EventHistory,
	MapData,
	UsageReport,
	UserProfile,
	UserNotificationPreferencesResponse,
	UpdateUserNotificationPreferencesRequestBody,
	CountryAvailableSms,
} from 'src/app/models/model';
import { NewUserResponse } from 'src/app/models/response';
import { Environment } from 'src/environments/environment';
import { ENV } from 'src/environment.provider';
import { AppVisit, AppVisitNote, AppVisitPhoto } from '../models/data/appVisit';
import { LoadingIndicatorService } from './loading-indicator.service';
import Property = Components.Schemas.Location;
import Contract = Components.Schemas.Contract;
import AddOnBillingSettings = Components.Schemas.AddOnBillingSettings;
import UserAuthentication = Components.Schemas.UserAuthentication;
import TokenValidity = Components.Schemas.TokenValidity;
import NumericToken = Components.Schemas.NumericToken;
import AccessReportFilterOptions = Components.Schemas.AccessReportFilterOptions;
import KeyDetectionReportFilterOptions = Components.Schemas.KeyDetectionReportFilterOptions;
import CodeGenReportFilterOptions = Components.Schemas.CodeGenReportFilterOptions;
import InventoryReportFilterOptions = Components.Schemas.InventoryReportFilterOptions;
import PropertyReportFilterOptions = Components.Schemas.PropertyReportFilterOptions;
import DeviceAlert = Components.Schemas.DeviceAlert;
import DeviceAlertTotal = Components.Schemas.DeviceAlertTotal;
import UsageReportFilterOptions = Components.Schemas.UsageReportFilterOptions;
import CompanyUserDeviceGroupDeviceCount = Components.Schemas.CompanyUserDeviceGroupDeviceCount;
import AuditReportRecord = Components.Schemas.AuditReportRecord;
import { Timezone } from '../models/timezone';
import FeatureFlag = Components.Schemas.FeatureFlag;
import Client = Components.Schemas.Client;
import CompanySetting = Components.Schemas.CompanySetting;
import { AlertReportFilterOptions } from '../ui/reports/alert-report-dialog/alert-report-dialog.component';
import { AlertReport } from '../ui/reports/alert-report-table/alert-report-table.component';
import { TransferDeviceOwnershipError } from '../models/data/deviceTransfer';
import { DeviceSettingsKV } from '../models/data/deviceSettings';
import {
	Contact,
	AddEditDeleteContactDetailResponse,
} from '../models/contacts';
import CompanyDetailBasedTaxId = Components.Schemas.CompanyWithTaxIdInfo;

@Injectable({
	providedIn: 'root',
})
export class ApiMockService {
	private static sentrilockCompanyId?: string;
	static router: Router;

	constructor(
		private readonly httpClient: HttpClient,
		private readonly router: Router,
		private readonly loadingService: LoadingIndicatorService,
		@Inject(ENV) private env: Environment
	) {
		ApiMockService.router = this.router;
	}

	private static handleError(response: HttpErrorResponse): Observable<never> {
		if (response.status === 401) {
			ApiMockService.router?.navigateByUrl('/login');
		}
		if (response.error !== undefined) {
			return throwError(() => response.error);
		} else {
			return throwError(() => `Unexpected error: ${response.error}`);
		}
	}

	/**
	 * RFC 3986 encodeURIComponent that also escapes `!'()*` (left literal by
	 * encodeURIComponent). Mirrors ApiService for DE5719 / KEY-1202.
	 */
	private strictEncodeURIComponent(value: string | number | boolean): string {
		return encodeURIComponent(value).replace(
			/[!'()*]/g,
			(c) => '%' + c.charCodeAt(0).toString(16).toUpperCase()
		);
	}

	private generateHttpParams(queryParams: QueryParam[] = []): HttpParams {
		const params: {
			[param: string]:
				string | number | boolean | ReadonlyArray<string | number | boolean>;
		} = {};
		queryParams.forEach(
			(p) =>
				(params[p.param] = Array.isArray(p.value)
					? p.value.map((val) => this.strictEncodeURIComponent(val))
					: this.strictEncodeURIComponent(p.value))
		);
		const httpParams = new HttpParams();
		return httpParams.appendAll(params);
	}

	async login(
		email: string,
		password: string,
		sessionKeepActiveTimeSeconds?: number
	): Promise<UserAuthentication> {
		this.loadingService.start(ApiLoadingKeys.login);
		const body = { email, password, sessionKeepActiveTimeSeconds };
		const resp = {
			loginToken: 'Test12',
			userId: '123',
		};
		this.loadingService.stop(ApiLoadingKeys.login);
		return resp;
	}

	async requestMfaToken(method: MfaMethod): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.requestMfaToken);
		const url = `${this.env.apiUrl}/api/v1/user/authenticate/mfa/request?method=${method}`;

		this.loadingService.stop(ApiLoadingKeys.requestMfaToken);
	}

	async verifyMfaToken(token: string): Promise<UserAuthentication> {
		this.loadingService.start(ApiLoadingKeys.verifyMfaToken);
		const url = `${this.env.apiUrl}/api/v1/user/authenticate/mfa/validate?token=${token}`;
		const resp = {
			loginToken: 'Test12',
			userId: '123',
		};

		this.loadingService.stop(ApiLoadingKeys.verifyMfaToken);
		return resp;
	}

	async updateCustomClaims(
		activeEntityRelationshipId: string
	): Promise<UserAuthentication> {
		this.loadingService.start(ApiLoadingKeys.updateCustomClaims);
		const body = { activeEntityRelationshipId };
		const resp = {
			loginToken: 'Test12',
			userId: '123',
		};
		this.loadingService.stop(ApiLoadingKeys.updateCustomClaims);
		return resp;
	}

	async getSentrilockCompanyId(): Promise<string> {
		// Sentrilock Company ID won't change, so only get it once and store the value
		if (!ApiMockService.sentrilockCompanyId) {
			this.loadingService.start(ApiLoadingKeys.getSentrilockCompanyId);
			const url = `${this.env.apiUrl}/api/v1/company/sentrilockCompanyId`;
			const response = '123';
			this.loadingService.stop(ApiLoadingKeys.getSentrilockCompanyId);
			ApiMockService.sentrilockCompanyId = response;
		}
		return ApiMockService.sentrilockCompanyId;
	}

	async sendPasswordResetEmail(email: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.sendPasswordResetEmail);
		const url = `${this.env.apiUrl}/api/v1/user/password/reset`;

		this.loadingService.stop(ApiLoadingKeys.sendPasswordResetEmail);
	}

	async checkPasswordResetTokenStatus(
		token: string
	): Promise<PasswordResetTokenStatus> {
		this.loadingService.start(ApiLoadingKeys.checkPasswordResetTokenStatus);
		const url = `${this.env.apiUrl}/api/v1/user/password/reset?token=${token}`;
		const response = {
			body: {
				data: {
					status: 'ok',
				},
			},
		};
		this.loadingService.stop(ApiLoadingKeys.checkPasswordResetTokenStatus);

		const tokenStatus = response.body?.data as PasswordResetTokenStatus;
		if (tokenStatus !== null) {
			return tokenStatus;
		} else {
			throw new Error('Interval Server Error');
		}
	}

	async resetPassword(password: string, token: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.resetPassword);
		const url = `${this.env.apiUrl}/api/v1/user/password/reset`;

		this.loadingService.stop(ApiLoadingKeys.resetPassword);
	}

	async changePassword(
		oldPassword: string,
		newPassword: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.changePassword);
		const url = `${this.env.apiUrl}/api/v1/user/password`;

		this.loadingService.stop(ApiLoadingKeys.changePassword);
	}

	async getUserProfile(handleError = true): Promise<UserProfile> {
		this.loadingService.start(ApiLoadingKeys.getUserProfile);
		const resp = {
			user: {
				id: '215894751875043328',
				sluuId: '1377184',
				email: 'j********y@gmail.com',
				firstName: 'Jesse',
				lastName: 'Yeary',
				phoneNumber: {
					rawPhoneNumber: '*******5708',
					countryCode: 'us',
				},
				active: true,
				verified: true,
			},
			availableAssignedRoles: [
				{
					id: '891',
					entityRelationshipId: '2788',
					roleId: '1',
					role: {
						id: '1',
						name: 'Oracle',
						ownerEntityId: '1',
						creatorName: 'System Admin',
						isUniversal: true,
						isSentriLockOnly: true,
						isOracle: true,
						isCompanyAdmin: false,
						isSupport: false,
					},
					utcStartDate: '2022-08-19T18:08:47.000Z',
					active: true,
				},
			],
		};
		this.loadingService.stop(ApiLoadingKeys.getUserProfile);
		return resp;
	}

	async updateUser(
		userId: string,
		user: Partial<User>
	): Promise<UserWithOptionalErrorMessage> {
		this.loadingService.start(ApiLoadingKeys.updateUser);
		user = {
			id: '',
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			phoneNumber: user.phoneNumber,
			active: user.active ?? true,
		};

		if (
			user.phoneNumber?.rawPhoneNumber &&
			!user.phoneNumber?.rawPhoneNumber?.startsWith('+')
		) {
			user.phoneNumber.rawPhoneNumber = `+${user.phoneNumber.rawPhoneNumber}`;
		}

		const resp = {
			id: '215894751875043328',
			sluuId: '1377184',
			email: 'j********y@gmail.com',
			firstName: 'Jesse',
			lastName: 'Yeary',
			phoneNumber: {
				rawPhoneNumber: '*******5708',
				countryCode: 'us',
			},
			active: true,
			verified: true,
		};

		this.loadingService.stop(ApiLoadingKeys.updateUser);
		return resp;
	}

	async updateUserProfile(
		updateUserProfileObject: UpdateUserProfile
	): Promise<UserProfile> {
		this.loadingService.start(ApiLoadingKeys.updateUserProfile);
		const resp = {
			user: {
				id: '215894751875043328',
				sluuId: '1377184',
				email: 'j********y@gmail.com',
				firstName: 'Jesse',
				lastName: 'Yeary',
				phoneNumber: {
					rawPhoneNumber: '*******5708',
					countryCode: 'us',
				},
				active: true,
				verified: true,
			},
			availableAssignedRoles: [
				{
					id: '891',
					entityRelationshipId: '2788',
					roleId: '1',
					role: {
						id: '1',
						name: 'Oracle',
						ownerEntityId: '1',
						creatorName: 'System Admin',
						isUniversal: true,
						isSentriLockOnly: true,
						isOracle: true,
						isCompanyAdmin: false,
						isSupport: false,
					},
					utcStartDate: '2022-08-19T18:08:47.000Z',
					active: true,
				},
			],
		};
		this.loadingService.stop(ApiLoadingKeys.updateUserProfile);
		return resp;
	}

	async getUserNotificationPreferences(
		handleError = true
	): Promise<UserNotificationPreferencesResponse> {
		this.loadingService.start(ApiLoadingKeys.getUserNotificationPreferences);
		const resp = {
			userNotificationPreferences: [
				{
					id: '123',
					userId: '987',
					deviceGroupId: '456',
					deviceGroupName: 'DG 456',
					userDeviceGroupNotificationPreferences: [
						{
							id: '1',
							userDeviceGroupId: '123',
							userNotificationEventTypeId: '1',
							receiveEmail: true,
							receiveSms: true,
							userNotificationEventType: {
								id: '1',
								nameTranslationKey: 'lowBatteryNotification',
							},
						},
						{
							id: '2',
							userDeviceGroupId: '123',
							userNotificationEventTypeId: '2',
							receiveEmail: true,
							receiveSms: false,
							userNotificationEventType: {
								id: '2',
								nameTranslationKey: 'missingKeyNotification',
							},
						},
						{
							userDeviceGroupId: '123',
							userNotificationEventTypeId: '3',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '3',
								nameTranslationKey: 'keyNotDetectedNotification',
							},
						},
					],
				},
				{
					userId: '987',
					deviceGroupId: '1234',
					deviceGroupName: 'DG 1234',
					userDeviceGroupNotificationPreferences: [
						{
							userNotificationEventTypeId: '1',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '1',
								nameTranslationKey: 'lowBatteryNotification',
							},
						},
						{
							userNotificationEventTypeId: '2',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '2',
								nameTranslationKey: 'missingKeyNotification',
							},
						},
						{
							userNotificationEventTypeId: '3',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '3',
								nameTranslationKey: 'keyNotDetectedNotification',
							},
						},
					],
				},
			],
		};
		this.loadingService.stop(ApiLoadingKeys.getUserNotificationPreferences);
		return resp;
	}

	async updateUserNotificationPreferences(
		userNotificationPreferences: UpdateUserNotificationPreferencesRequestBody
	): Promise<UserNotificationPreferencesResponse> {
		this.loadingService.start(ApiLoadingKeys.updateUserNotificationPreferences);
		const resp = {
			userNotificationPreferences: [
				{
					id: '123',
					userId: '987',
					deviceGroupId: '456',
					deviceGroupName: 'DG 456',
					userDeviceGroupNotificationPreferences: [
						{
							id: '1',
							userDeviceGroupId: '123',
							userNotificationEventTypeId: '1',
							receiveEmail: true,
							receiveSms: true,
							userNotificationEventType: {
								id: '1',
								nameTranslationKey: 'lowBatteryNotification',
							},
						},
						{
							id: '2',
							userDeviceGroupId: '123',
							userNotificationEventTypeId: '2',
							receiveEmail: true,
							receiveSms: false,
							userNotificationEventType: {
								id: '2',
								nameTranslationKey: 'missingKeyNotification',
							},
						},
						{
							userDeviceGroupId: '123',
							userNotificationEventTypeId: '3',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '3',
								nameTranslationKey: 'keyNotDetectedNotification',
							},
						},
					],
				},
				{
					userId: '987',
					deviceGroupId: '1234',
					deviceGroupName: 'DG 1234',
					userDeviceGroupNotificationPreferences: [
						{
							userNotificationEventTypeId: '1',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '1',
								nameTranslationKey: 'lowBatteryNotification',
							},
						},
						{
							userNotificationEventTypeId: '2',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '2',
								nameTranslationKey: 'missingKeyNotification',
							},
						},
						{
							userNotificationEventTypeId: '3',
							receiveEmail: false,
							receiveSms: false,
							userNotificationEventType: {
								id: '3',
								nameTranslationKey: 'keyNotDetectedNotification',
							},
						},
					],
				},
			],
		};
		this.loadingService.stop(ApiLoadingKeys.updateUserNotificationPreferences);
		return resp;
	}

	async validateEmail(email: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.validateEmail);
		const resp = true;
		this.loadingService.stop(ApiLoadingKeys.validateEmail);
		return resp;
	}

	async requestEmailVerificationCode(newEmail: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.requestEmailVerificationCode);

		this.loadingService.stop(ApiLoadingKeys.requestEmailVerificationCode);
	}

	async verifyEmailVerificationCode(
		newEmail: string,
		token: NumericToken
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.verifyEmailVerificationCode);
		const resp = true;
		this.loadingService.stop(ApiLoadingKeys.verifyEmailVerificationCode);
		return resp;
	}

	async getUserDetails(uid: string): Promise<User> {
		this.loadingService.start(ApiLoadingKeys.getUserDetails);
		const resp = {
			id: '215894751875043328',
			sluuId: '1377184',
			email: 'j********y@gmail.com',
			firstName: 'Jesse',
			lastName: 'Yeary',
			phoneNumber: {
				rawPhoneNumber: '*******5708',
				countryCode: 'us',
			},
			active: true,
			verified: true,
		};

		this.loadingService.stop(ApiLoadingKeys.getUserDetails);
		return resp;
	}

	async getUserAndRole(
		token: string,
		userId: string
	): Promise<{ user: User; roleName: string; roleCompanyName: string }> {
		this.loadingService.start(ApiLoadingKeys.getUserAndRole);
		const resp = {
			user: {
				id: '215894751875043328',
				sluuId: '1377184',
				email: 'j********y@gmail.com',
				firstName: 'Jesse',
				lastName: 'Yeary',
				phoneNumber: {
					rawPhoneNumber: '*******5708',
					countryCode: 'us',
				},
				active: true,
				verified: true,
			},
			roleName: 'Oracle',
			roleCompanyName: 'SentriKey Access',
		};
		this.loadingService.stop(ApiLoadingKeys.getUserAndRole);
		return resp;
	}

	async getUserEventHistory(
		id: string,
		queryParams?: QueryParam[]
	): Promise<EventHistory[]> {
		this.loadingService.start(ApiLoadingKeys.getUserEventHistory);
		const params = this.generateHttpParams(queryParams);
		const resp: EventHistory[] | PromiseLike<EventHistory[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getUserEventHistory);
		return resp;
	}

	async getAllUsers(queryParams?: QueryParam[]): Promise<User[]> {
		this.loadingService.start(ApiLoadingKeys.getAllUsers);
		let params: HttpParams = this.generateHttpParams(queryParams);
		params = params.set('allUsers', 'true');

		const resp: User[] | PromiseLike<User[]> = [];

		this.loadingService.stop(ApiLoadingKeys.getAllUsers);
		return resp;
	}

	async resendUserRegistration(userId: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.resendUserRegistration);

		this.loadingService.stop(ApiLoadingKeys.resendUserRegistration);
	}

	async searchCompaniesPublicDirectory(
		queryParams?: QueryParam[]
	): Promise<Company[]> {
		this.loadingService.start(ApiLoadingKeys.searchCompaniesPublicDirectory);
		const params = this.generateHttpParams(queryParams);
		const resp: Company[] | PromiseLike<Company[]> = [];
		this.loadingService.stop(ApiLoadingKeys.searchCompaniesPublicDirectory);
		return resp;
	}

	async getCompanyById(
		id: string
	): Promise<{ company: Company; relationship?: EntityRelationship }> {
		this.loadingService.start(ApiLoadingKeys.getCompanyById);
		const resp = {
			company: {
				id: '238735814733991936',
				name: 'Car Cleaners',
				taxId: '543211',
				address: {
					id: '1083',
					address1: '8050 Arbor Square Dr',
					address2: '',
					locality: 'Mason',
					stateProvinceRegion: 'Ohio',
					postalCode: '45040',
					countryCode: 'US',
					geoCoordinates: {
						latitude: 0,
						longitude: 0,
					},
				},
				active: true,
				slugId: '8392',
			},
			relationship: {
				id: '2879',
				childEntityId: '238735814733991936',
				parentEntityId: '51463728100540417',
				active: true,
			},
		};
		this.loadingService.stop(ApiLoadingKeys.getCompanyById);
		return resp;
	}

	async getCompanyByInfo(queryParams?: QueryParam[]): Promise<Company[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyByInfo);
		const params = this.generateHttpParams(queryParams);
		const resp: Company[] | PromiseLike<Company[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getCompanyByInfo);
		return resp;
	}

	async verifyTaxIdBasedOnValue(
		queryParams?: QueryParam[]
	): Promise<CompanyDetailBasedTaxId[]> {
		this.loadingService.start(
			ApiLoadingKeys.getVerificationOfTaxIdBasedOnValue
		);
		const params = this.generateHttpParams(queryParams);
		const resp:
			CompanyDetailBasedTaxId[] | PromiseLike<CompanyDetailBasedTaxId> = [];
		this.loadingService.stop(ApiLoadingKeys.getVerificationOfTaxIdBasedOnValue);
		return resp;
	}

	async editCompanyById(
		id: string,
		requestBody: EditCompanyRequestBody
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editCompanyById);

		this.loadingService.stop(ApiLoadingKeys.editCompanyById);
	}

	async updateCompanyRelationship(
		requestBody: UpdateCompanyRelationshipDetailsRequestBody
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateCompanyRelationship);

		this.loadingService.stop(ApiLoadingKeys.updateCompanyRelationship);
	}

	async getCompanyRelationships(
		queryParams?: QueryParam[]
	): Promise<CompanyEntityRelationship[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyRelationships);
		const params = this.generateHttpParams(queryParams);
		const resp:
			CompanyEntityRelationship[] | PromiseLike<CompanyEntityRelationship[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.getCompanyRelationships);
		return resp;
	}

	async getCompanyRelationshipsByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<CompanyEntityRelationship[]> {
		this.loadingService.start(
			ApiLoadingKeys.getCompanyRelationshipsByCompanyId
		);
		const params = this.generateHttpParams(queryParams);
		const resp:
			CompanyEntityRelationship[] | PromiseLike<CompanyEntityRelationship[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.getCompanyRelationshipsByCompanyId);
		return resp;
	}

	async createCompanyRelationship(
		requestBody: CreateCompanyEntityRelationshipRequestBody,
		parentCompanyEntityId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.createCompanyRelationship);

		this.loadingService.stop(ApiLoadingKeys.createCompanyRelationship);
	}

	async createNewCompany(
		requestBody: CreateCompanyRequestBody
	): Promise<Company> {
		this.loadingService.start(ApiLoadingKeys.createNewCompany);
		const resp = {
			id: '238735814733991936',
			name: 'Car Cleaners',
			taxId: '543211',
			address: {
				id: '1083',
				address1: '8050 Arbor Square Dr',
				address2: '',
				locality: 'Mason',
				stateProvinceRegion: 'Ohio',
				postalCode: '45040',
				countryCode: 'US',
				geoCoordinates: {
					latitude: 0,
					longitude: 0,
				},
			},
			active: true,
			slugId: '8392',
		};
		this.loadingService.stop(ApiLoadingKeys.createNewCompany);
		return resp;
	}

	async getCompanyRolesByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<RoleDataItem[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyRolesByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp: RoleDataItem[] | PromiseLike<RoleDataItem[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getCompanyRolesByCompanyId);
		return resp;
	}

	async createDeviceGroup(
		companyId: string,
		groupName: string,
		deviceIds: string[]
	): Promise<{ deviceGroupId: string }> {
		this.loadingService.start(ApiLoadingKeys.createDeviceGroup);
		const body: { groupName: string; deviceIds: string[] } = {
			groupName,
			deviceIds,
		};
		const resp = { deviceGroupId: '123' };
		this.loadingService.stop(ApiLoadingKeys.createDeviceGroup);
		return resp;
	}

	async updateDeviceGroupMembership(
		deviceGroupId: string,
		deviceIds: string[],
		action: 'add' | 'remove'
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateDeviceGroupMembership);

		this.loadingService.stop(ApiLoadingKeys.updateDeviceGroupMembership);
	}

	async getSystemCapabilitiesByRoleId(
		roleId: string
	): Promise<SystemCapabilityItem[]> {
		this.loadingService.start(ApiLoadingKeys.getSystemCapabilitiesByRoleId);
		const resp: SystemCapabilityItem[] | PromiseLike<SystemCapabilityItem[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.getSystemCapabilitiesByRoleId);
		return resp;
	}

	async getRoleCapabilities(): Promise<SystemCapability[]> {
		this.loadingService.start(ApiLoadingKeys.getRoleCapabilities);
		const resp: SystemCapability[] | PromiseLike<SystemCapability[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getRoleCapabilities);
		return resp;
	}

	async getMyRoles(queryParams?: QueryParam[]): Promise<RoleDataItem[]> {
		this.loadingService.start(ApiLoadingKeys.getMyRoles);
		const params = this.generateHttpParams(queryParams);
		const resp: RoleDataItem[] | PromiseLike<RoleDataItem[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getMyRoles);
		return resp;
	}

	async getRoleById(
		roleId: string
	): Promise<{ role: Role; systemCapabilities: SystemCapabilityItem[] }> {
		this.loadingService.start(ApiLoadingKeys.getRoleById);
		const resp = {
			role: {
				id: '1',
				name: 'Oracle',
				ownerEntityId: '1',
				creatorName: 'System Admin',
				isUniversal: true,
				isSentriLockOnly: true,
				isOracle: true,
				isCompanyAdmin: false,
				isSupport: false,
			},
			systemCapabilities: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getRoleById);
		return resp;
	}

	async updateRole(
		roleId: string,
		roleName: string,
		systemCapabilityItems: SystemCapabilityItem[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateRole);
		const systemCapabilityIds = systemCapabilityItems
			.filter((it) => it.granted)
			.map((it) => it.systemCapability.id);

		this.loadingService.stop(ApiLoadingKeys.updateRole);
	}

	async getAssignedRoleUsers(
		roleId: string,
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<User[]> {
		this.loadingService.start(ApiLoadingKeys.getAssignedRoleUsers);
		let params = this.generateHttpParams(queryParams);
		params = params.set('companyId', companyId);

		const resp: User[] | PromiseLike<User[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getAssignedRoleUsers);
		return resp;
	}

	async createNewRole(
		companyId: string,
		name: string,
		systemCapabilityItems: SystemCapabilityItem[]
	): Promise<Role> {
		this.loadingService.start(ApiLoadingKeys.createNewRole);
		const systemCapabilities = systemCapabilityItems
			.filter((it) => it.granted === true)
			.map((it) => it.systemCapability.id);
		const resp = {
			id: '1',
			name: 'Oracle',
			ownerEntityId: '1',
			creatorName: 'System Admin',
			isUniversal: true,
			isSentriLockOnly: true,
			isOracle: true,
			isCompanyAdmin: false,
			isSupport: false,
		};
		this.loadingService.stop(ApiLoadingKeys.createNewRole);
		return resp;
	}

	async assignRoleToUserId(
		userIds: string[],
		roleId: string,
		companyId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.assignRoleToUserId);
		const body = { roleId, userIds, companyId };

		this.loadingService.stop(ApiLoadingKeys.assignRoleToUserId);
	}

	async unassignRoleFromUserId(
		userIds: string[],
		roleId: string,
		companyId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.unassignRoleFromUserId);
		const body = { userIds, companyId };

		this.loadingService.stop(ApiLoadingKeys.unassignRoleFromUserId);
	}

	async getCompanyUsersByCompanyId(
		id: string,
		queryParams?: QueryParam[]
	): Promise<UserWithRoleName[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyUsersByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp: UserWithRoleName[] | PromiseLike<UserWithRoleName[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getCompanyUsersByCompanyId);
		return resp;
	}

	async getMyUsers(queryParams?: QueryParam[]): Promise<User[]> {
		this.loadingService.start(ApiLoadingKeys.getMyUsers);
		const params = this.generateHttpParams(queryParams);
		const resp: User[] | PromiseLike<User[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getMyUsers);
		return resp;
	}

	async getCompanyPropertiesByCompanyId(
		id: string,
		queryParams?: QueryParam[]
	): Promise<{ property: Property; deviceCount: number }[]> {
		this.loadingService.start(ApiLoadingKeys.getCompanyPropertiesByCompanyId);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| { property: Property; deviceCount: number }[]
			| PromiseLike<{ property: Property; deviceCount: number }[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getCompanyPropertiesByCompanyId);
		return resp;
	}

	async getBillingContracts(queryParams?: QueryParam[]): Promise<Contract[]> {
		this.loadingService.start(ApiLoadingKeys.getContracts);
		const params = this.generateHttpParams(queryParams);
		const resp: Contract[] | PromiseLike<Contract[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getContracts);
		return resp;
	}

	async getBillingContractById(id: string): Promise<{
		billingSettingsResult: Contract;
		addOnBillingSettingsResult: AddOnBillingSettings[];
	}> {
		this.loadingService.start(ApiLoadingKeys.getSingleContract);
		const resp:
			| {
					billingSettingsResult: Contract;
					addOnBillingSettingsResult: AddOnBillingSettings[];
			  }
			| PromiseLike<{
					billingSettingsResult: Contract;
					addOnBillingSettingsResult: AddOnBillingSettings[];
			  }> = {
			billingSettingsResult: {
				id: '3000',
				company: {
					id: '3001',
					name: 'Cleaning Company',
				},
				netsuiteCustomerInternalId: '48943299',
				contractName: 'Some contract name',
				contractId: '30',
				unitOfMeassureTypeId: '1',
				currencyTypeId: '1',
				unitPrice: 3.39,
				billingStartDate: new Date(
					new Date().getFullYear(),
					new Date().getMonth() + 1,
					15
				).toISOString(),
				billingTriggerTypeId: '1',
				duration: 3,
				annualIncreasePercent: 3.3,
				areDevicesLeased: true,
				isMonthlyAutoRenewed: false,
				status: 'upcoming',
			},
			addOnBillingSettingsResult: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getSingleContract);
		return resp;
	}

	async saveBillingContract(contract: Contract): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.saveContract);
		const apiURL = `${this.env.apiUrl}/api/v1/billingcontract`;
		const body = contract;
		this.loadingService.stop(ApiLoadingKeys.saveContract);
	}

	async createNewProperty(
		requestBody: CreatePropertyRequestBody
	): Promise<Property> {
		this.loadingService.start(ApiLoadingKeys.createNewProperty);
		const resp = {
			id: '203',
			name: "Jesse's House",
			companyId: '51463728100540417',
			addressId: '1060',
			address: {
				id: '1060',
				address1: '8476 Deerway Dr',
				address2: '',
				locality: 'Cincinnati',
				stateProvinceRegion: 'OH',
				postalCode: '45236',
				countryCode: 'US',
				geoCoordinates: {
					latitude: 39.2118408,
					longitude: -84.3825828,
				},
			},
			active: true,
			externalId: '',
			pendingInstalls: 0,
		};
		this.loadingService.stop(ApiLoadingKeys.createNewProperty);
		return resp;
	}

	async getProperties(
		queryParams?: QueryParam[]
	): Promise<{ property: Property; deviceCount: number }[]> {
		this.loadingService.start(ApiLoadingKeys.getProperties);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| { property: Property; deviceCount: number }[]
			| PromiseLike<{ property: Property; deviceCount: number }[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getProperties);
		return resp;
	}

	async getClients(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<Client[]> {
		this.loadingService.start(ApiLoadingKeys.getClients);
		const params = this.generateHttpParams(queryParams);
		const resp: Client[] = [];
		this.loadingService.stop(ApiLoadingKeys.getClients);
		return resp;
	}

	async getClientProperty(
		clientId: string
	): Promise<{ property: Property; deviceCount: number }[]> {
		this.loadingService.start(ApiLoadingKeys.getClientProperty);
		const resp: { property: Property; deviceCount: number }[] = [];
		this.loadingService.stop(ApiLoadingKeys.getClientProperty);
		return resp;
	}

	async getClientDevices(
		clientId: string,
		queryParams?: QueryParam[]
	): Promise<{ device: Device; groupNames?: string[] }[]> {
		this.loadingService.start(ApiLoadingKeys.getClientDevices);
		const params = this.generateHttpParams(queryParams);
		const resp: { device: Device; groupNames?: string[] }[] = [];
		this.loadingService.stop(ApiLoadingKeys.getClientDevices);
		return resp;
	}

	// Get : contacts for the notifications :
	async getContactList(
		clientId: string,
		queryParams?: QueryParam[]
	): Promise<Contact[]> {
		this.loadingService.start(ApiLoadingKeys.getContacts);
		const params = this.generateHttpParams(queryParams);
		const resp: Contact[] = [];
		this.loadingService.stop(ApiLoadingKeys.getContacts);
		return resp;
	}

	// Post : Add / Edit contact details for notifications :
	async postContactDetails(
		clientId: string,
		queryParams?: QueryParam[]
	): Promise<AddEditDeleteContactDetailResponse> {
		this.loadingService.start(ApiLoadingKeys.getContacts);
		const params = this.generateHttpParams(queryParams);
		const resp: AddEditDeleteContactDetailResponse = {};
		this.loadingService.stop(ApiLoadingKeys.getContacts);
		return resp;
	}

	// Delete : contact details for notifications :
	async deleteContactDetail(
		clientId: string,
		queryParams?: QueryParam[]
	): Promise<AddEditDeleteContactDetailResponse> {
		this.loadingService.start(ApiLoadingKeys.getContacts);
		const params = this.generateHttpParams(queryParams);
		const resp: AddEditDeleteContactDetailResponse = {};
		this.loadingService.stop(ApiLoadingKeys.getContacts);
		return resp;
	}

	async getProperty(propertyId: string): Promise<Property> {
		this.loadingService.start(ApiLoadingKeys.getProperty);
		const resp = {
			id: '203',
			name: "Jesse's House",
			companyId: '51463728100540417',
			addressId: '1060',
			address: {
				id: '1060',
				address1: '8476 Deerway Dr',
				address2: '',
				locality: 'Cincinnati',
				stateProvinceRegion: 'OH',
				postalCode: '45236',
				countryCode: 'US',
				geoCoordinates: {
					latitude: 39.2118408,
					longitude: -84.3825828,
				},
			},
			active: true,
			externalId: '',
			pendingInstalls: 0,
		};
		this.loadingService.stop(ApiLoadingKeys.getProperty);
		return resp;
	}

	async getPropertyEventHistory(
		id: string,
		queryParams?: QueryParam[]
	): Promise<EventHistory[]> {
		this.loadingService.start(ApiLoadingKeys.getPropertyEventHistory);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| Components.Schemas.EventHistory[]
			| PromiseLike<Components.Schemas.EventHistory[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getPropertyEventHistory);
		return resp;
	}

	async editPropertyById(
		id: string,
		editRequest: EditPropertyRequestBody
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editPropertyById);

		this.loadingService.stop(ApiLoadingKeys.editPropertyById);
	}

	async editDeviceById(
		id: string,
		editRequest: EditDeviceRequestBody
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editDeviceById);

		this.loadingService.stop(ApiLoadingKeys.editDeviceById);
	}

	async saveDeviceInstallationPhoto(
		deviceId: string,
		file: File,
		caption: string
	): Promise<DeviceInstallationPhoto> {
		this.loadingService.start(ApiLoadingKeys.saveDeviceInstallationPhoto);
		const photo = {
			id: '0',
			deviceInstallationId: '0',
			photoUrl: '',
			caption,
			utcTimeTaken: new Date().toISOString(),
		} as DeviceInstallationPhoto;
		this.loadingService.stop(ApiLoadingKeys.saveDeviceInstallationPhoto);
		return photo;
	}

	async getAllDevices(
		queryParams?: QueryParam[]
	): Promise<
		{ device: Device; groupNames?: string[]; source?: DeviceAccessSource }[]
	> {
		this.loadingService.start(ApiLoadingKeys.getAllDevices);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| { device: Device; groupNames?: string[]; source?: DeviceAccessSource }[]
			| PromiseLike<
					{
						device: Device;
						groupNames?: string[];
						source?: DeviceAccessSource;
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getAllDevices);
		return resp;
	}

	async getDevicesByCompanyId(
		companyId: string,
		queryParams?: QueryParam[]
	): Promise<
		{
			device: Device;
			groupNames?: string[];
			source?: DeviceAccessSource;
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDevicesByCompanyId);
		const url = `${this.env.apiUrl}/api/v1/company/${companyId}/device`;
		const params = this.generateHttpParams(queryParams);

		const resp:
			| { device: Device; groupNames?: string[]; source?: DeviceAccessSource }[]
			| PromiseLike<
					{
						device: Device;
						groupNames?: string[];
						source?: DeviceAccessSource;
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getDevicesByCompanyId);
		return resp;
	}

	async getDevicesByPropertyId(
		propertyId: string,
		queryParams?: QueryParam[]
	): Promise<{ device: Device; groupNames?: string[] }[]> {
		this.loadingService.start(ApiLoadingKeys.getDevicesByPropertyId);
		const url = `${this.env.apiUrl}/api/v1/properties/${propertyId}/device`;
		const params = this.generateHttpParams(queryParams);
		const resp:
			| { device: Device; groupNames?: string[] }[]
			| PromiseLike<{ device: Device; groupNames?: string[] }[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getDevicesByPropertyId);
		return resp;
	}

	// tslint:disable-next-line: max-line-length
	async getDevicesByDeviceGroupId(
		deviceGroupId: string,
		queryParams?: QueryParam[]
	): Promise<
		{ device: Device; deviceGroupDeviceStatus: DeviceGroupDeviceStatus }[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDevicesByDeviceGroupId);
		const url = `${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}/device`;
		const params = this.generateHttpParams(queryParams);
		const resp:
			| { device: Device; deviceGroupDeviceStatus: DeviceGroupDeviceStatus }[]
			| PromiseLike<
					{ device: Device; deviceGroupDeviceStatus: DeviceGroupDeviceStatus }[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getDevicesByDeviceGroupId);
		return resp;
	}

	async getAvailableDevices(
		deviceGroupId: string,
		queryParams?: QueryParam[]
	): Promise<{ device: Device; groupNames?: string[] }[]> {
		this.loadingService.start(ApiLoadingKeys.getAvailableDevices);
		const url = `${this.env.apiUrl}/api/v1/deviceGroup/${deviceGroupId}/device/available`;
		const params = this.generateHttpParams(queryParams);
		const resp:
			| { device: Device; groupNames?: string[] }[]
			| PromiseLike<{ device: Device; groupNames?: string[] }[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getAvailableDevices);
		return resp;
	}

	async getDeviceById(deviceId: string): Promise<Device> {
		this.loadingService.start(ApiLoadingKeys.getDeviceById);
		const resp = {
			id: '108',
			serialNumber: '1487743',
			decommissioned: false,
		};
		this.loadingService.stop(ApiLoadingKeys.getDeviceById);
		return resp;
	}

	async getWasDeviceOwner(deviceId: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.getWasDeviceOwner);
		this.loadingService.stop(ApiLoadingKeys.getWasDeviceOwner);
		return false;
	}

	async getDeviceBySerialNumber(serialNumber: string): Promise<Device> {
		this.loadingService.start(ApiLoadingKeys.getDeviceBySerialNumber);
		const resp = {
			id: '108',
			serialNumber: '1487743',
			decommissioned: false,
		};
		this.loadingService.stop(ApiLoadingKeys.getDeviceBySerialNumber);
		return resp;
	}

	async getDeviceInstallationPhotos(
		deviceId: string
	): Promise<DeviceInstallationPhoto[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceInstallationPhotos);
		const resp:
			DeviceInstallationPhoto[] | PromiseLike<DeviceInstallationPhoto[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getDeviceInstallationPhotos);
		return resp;
	}

	async getInheritedDeviceGroupControlByDeviceId(deviceId: string): Promise<
		{
			control: DeviceControl;
			assignments: ControlAssignment[];
			devices: Device[];
		}[]
	> {
		this.loadingService.start(
			ApiLoadingKeys.getInheritedDeviceGroupControlById
		);
		const resp:
			| {
					control: DeviceControl;
					assignments: ControlAssignment[];
					devices: Device[];
			  }[]
			| PromiseLike<
					{
						control: DeviceControl;
						assignments: ControlAssignment[];
						devices: Device[];
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getInheritedDeviceGroupControlById);
		return resp;
	}

	async getAppNotes(
		deviceId: string,
		queryParams: QueryParam[]
	): Promise<
		{
			appVisit: AppVisit;
			appVisitNote: AppVisitNote;
			user: User;
			appVisitPhotos: AppVisitPhoto[];
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getAppNotes);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| {
					appVisit: AppVisit;
					appVisitNote: AppVisitNote;
					user: User;
					appVisitPhotos: AppVisitPhoto[];
			  }[]
			| PromiseLike<
					{
						appVisit: AppVisit;
						appVisitNote: AppVisitNote;
						user: User;
						appVisitPhotos: AppVisitPhoto[];
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getAppNotes);
		return resp;
	}

	async getDeviceNotes(
		queryParams: QueryParam[],
		deviceId: string
	): Promise<DeviceNoteResponse> {
		const apiURL = `${this.env.apiUrl}/api/v1/device/${deviceId}/deviceNote`;

		this.loadingService.start(ApiLoadingKeys.getAllDeviceNotes);
		const params = this.generateHttpParams(queryParams);
		const resp = {
			data: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getAllDeviceNotes);
		return resp;
	}

	async getAllDeviceNotes(
		queryParams: QueryParam[]
	): Promise<DeviceNoteResponse> {
		const apiURL = `${this.env.apiUrl}/api/v1/getAllNote`;

		this.loadingService.start(ApiLoadingKeys.getAllDeviceNotes);
		const params = this.generateHttpParams(queryParams);
		const resp = {
			data: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getAllDeviceNotes);
		return resp;
	}

	async addNote(
		deviceId?: string,
		appVisitNote?: AppVisitNote
	): Promise<string> {
		this.loadingService.start(ApiLoadingKeys.addNote);
		const apiURL = `${this.env.apiUrl}/api/v1/device/${deviceId}/deviceNote`;
		const body = appVisitNote;
		const resp = '12';
		this.loadingService.stop(ApiLoadingKeys.addNote);
		return resp;
	}

	async addDevice(serialNumberList: SerialNumber[]): Promise<SerialNumber[]> {
		this.loadingService.start(ApiLoadingKeys.addDevice);
		const apiURL = `${this.env.apiUrl}/api/v1/addDevice`;
		const body = serialNumberList;
		const serialNumberErrorList: SerialNumber[] = [
			{ serialNumber: '12345' },
			{ serialNumber: '34545' },
		];
		const resp = serialNumberErrorList;
		this.loadingService.stop(ApiLoadingKeys.addDevice);
		return resp;
	}

	async getDeviceProvisionError(): Promise<Device[]> {
		const apiURL = `${this.env.apiUrl}/api/v1/getDeviceProvisionError`;

		this.loadingService.start(ApiLoadingKeys.getDeviceProvisionError);
		const resp: Device[] = [
			{
				id: '1',
				serialNumber: '343434',
				decommissioned: false,
				isProvisioned: false,
				provisionMessage: 'test',
				utcProvisionDate: '2023-01-01T00:00:00.000Z',
			},
			{
				id: '2',
				serialNumber: '8978777',
				decommissioned: false,
				isProvisioned: false,
				provisionMessage: 'test',
				utcProvisionDate: '2023-10-05T00:00:00.000Z',
			},
		];
		this.loadingService.stop(ApiLoadingKeys.getDeviceProvisionError);
		return resp;
	}

	async getDeviceGroups(queryParams?: QueryParam[]): Promise<
		{
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroups);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| {
					group: DeviceGroup;
					deviceCount?: number;
					controlAssignmentCount?: number;
					source?: { companyId?: string; companyName?: string };
			  }[]
			| PromiseLike<
					{
						group: DeviceGroup;
						deviceCount?: number;
						controlAssignmentCount?: number;
						source?: { companyId?: string; companyName?: string };
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroups);
		return resp;
	}

	async getDeviceGroupsByCompanyId(
		_companyId: string,
		_queryParams?: QueryParam[]
	): Promise<
		{
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupsByCompanyId);
		const resp: {
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[] = [];
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupsByCompanyId);
		return resp;
	}

	async getDeviceGroupOptions(queryParams?: QueryParam[]): Promise<
		{
			group: DeviceGroup;
			deviceCount?: number;
			controlAssignmentCount?: number;
			source?: { companyId?: string; companyName?: string };
		}[]
	> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupOptionss);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| {
					group: DeviceGroup;
					deviceCount?: number;
					controlAssignmentCount?: number;
					source?: { companyId?: string; companyName?: string };
			  }[]
			| PromiseLike<
					{
						group: DeviceGroup;
						deviceCount?: number;
						controlAssignmentCount?: number;
						source?: { companyId?: string; companyName?: string };
					}[]
			  > = [
			{
				group: {
					id: '2433',
					companyId: '103194414333169690',
					name: '1379788_1266119',
					isSystemGenerated: 0,
					canSubauthorize: 'all',
					status: 'active',
				},
				deviceCount: 2,
				controlAssignmentCount: 0,
				source: {
					companyId: '103194414333169690',
					companyName: 'SentriKey Access',
				},
			},
			{
				group: {
					id: '2434',
					companyId: '294331128467296256',
					name: 'InheritedBoxes',
					isSystemGenerated: 0,
					canSubauthorize: 'all',
					status: 'active',
				},
				deviceCount: 2,
				controlAssignmentCount: 0,
				source: {
					companyId: '294331128467296256',
					companyName: 'A1 Company',
				},
			},
		];
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupOptionss);
		return resp;
	}

	async getDeviceGroupById(deviceGroupId: string): Promise<DeviceGroup> {
		this.loadingService.start(ApiLoadingKeys.getDeviceGroupById);
		const resp = {
			id: '2435',
			companyId: '51463728100540417',
			name: "Jesse's Boxes",
			isSystemGenerated: 0,
		};
		this.loadingService.stop(ApiLoadingKeys.getDeviceGroupById);
		return resp;
	}

	async getInheritedDeviceGroupControlById(deviceGroupId: string): Promise<
		{
			control: DeviceControl;
			assignments: ControlAssignment[];
			devices: Device[];
		}[]
	> {
		this.loadingService.start(
			ApiLoadingKeys.getInheritedDeviceGroupControlById
		);
		const resp:
			| {
					control: DeviceControl;
					assignments: ControlAssignment[];
					devices: Device[];
			  }[]
			| PromiseLike<
					{
						control: DeviceControl;
						assignments: ControlAssignment[];
						devices: Device[];
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getInheritedDeviceGroupControlById);
		return resp;
	}

	async getDeviceControls(
		queryParams?: QueryParam[]
	): Promise<{ deviceControl: DeviceControl }[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceControls);
		const params = this.generateHttpParams(queryParams);
		const resp:
			| {
					deviceControl: DeviceControl;
			  }[]
			| PromiseLike<
					{
						deviceControl: DeviceControl;
					}[]
			  > = [];
		this.loadingService.stop(ApiLoadingKeys.getDeviceControls);
		return resp;
	}

	async transferDeviceOwnership(
		deviceIds: string[],
		companyId: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.transferDeviceOwnership);

		this.loadingService.stop(ApiLoadingKeys.transferDeviceOwnership);
	}

	async massTransferDeviceOwnership(
		deviceSerialNumbers: string[],
		companyId: string
	): Promise<TransferDeviceOwnershipError[]> {
		this.loadingService.start(ApiLoadingKeys.transferDeviceOwnership);
		this.loadingService.stop(ApiLoadingKeys.transferDeviceOwnership);
		return [];
	}

	async getControlAssignments(
		queryParams?: QueryParam[]
	): Promise<ControlAssignment[]> {
		this.loadingService.start(ApiLoadingKeys.getControlAssignments);
		const params = this.generateHttpParams(queryParams);
		const resp: ControlAssignment[] | PromiseLike<ControlAssignment[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getControlAssignments);
		return resp;
	}

	async deleteControlAssignments(
		controlAssignmentIds: string[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.deleteControlAssignments);

		this.loadingService.stop(ApiLoadingKeys.deleteControlAssignments);
	}

	async editControlAssignments(
		controlAssignmentIds: string[],
		utcStartDate: string,
		utcEndDate?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editControlAssignments);

		this.loadingService.stop(ApiLoadingKeys.editControlAssignments);
	}

	async getControlAssignmentOptions(
		type: AssignableRelationshipType,
		search?: string
	): Promise<ControlAssignmentOption[]> {
		this.loadingService.start(ApiLoadingKeys.getControlAssignmentOptions);
		const queryParams: QueryParam[] = [{ param: 'type', value: type }];
		const params = this.generateHttpParams(queryParams);
		const resp:
			ControlAssignmentOption[] | PromiseLike<ControlAssignmentOption[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getControlAssignmentOptions);
		return resp;
	}

	async createDeviceControlsAndControlAssignments(
		deviceControl: DeviceControl,
		controlAssignments: ControlAssignment[],
		parentControlAssignmentId?: string
	): Promise<void> {
		const body: Components.RequestBodies.CreateDeviceControlAndControlAssignmentsRequestBody =
			{ deviceControl, controlAssignments, parentControlAssignmentId };

		this.loadingService.start(
			ApiLoadingKeys.createDeviceControlsAndControlAssignments
		);

		this.loadingService.stop(
			ApiLoadingKeys.createDeviceControlsAndControlAssignments
		);
	}

	async getOneDayCode(
		id: string,
		queryParams?: QueryParam[]
	): Promise<OneDayCodeListResponse[]> {
		this.loadingService.start(ApiLoadingKeys.getOneDayCode);
		const params = this.generateHttpParams(queryParams);
		const resp:
			OneDayCodeListResponse[] | PromiseLike<OneDayCodeListResponse[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getOneDayCode);
		return resp;
	}

	async getAccessCode(
		id: string,
		queryParams?: QueryParam[]
	): Promise<AccessCodeListResponse[]> {
		this.loadingService.start(ApiLoadingKeys.getAccessCode);
		const params = this.generateHttpParams(queryParams);
		const resp:
			AccessCodeListResponse[] | PromiseLike<AccessCodeListResponse[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getAccessCode);
		return resp;
	}

	async requestAndSaveOneDayCode(
		id: string,
		oneDayCodeGen3: OneDayCodeGen3,
		notifyByEmail: boolean,
		includeInEmail: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		},
		notifyBySms: boolean,
		includeInSms: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		}
	): Promise<OneDayCodeResponse> {
		this.loadingService.start(ApiLoadingKeys.requestAndSaveOneDayCode);
		const resp: OneDayCodeResponse = {
			data: {
				id: '6236',
				deviceId: '85',
				device: {
					id: '85',
					serialNumber: '1417984',
					decommissioned: false,
				},
				oneDayCode: '10286219',
				creatorId: '215894751875043328',
				creatorCompanyId: '1234',
				creator: {
					id: '215894751875043328',
					firstName: 'Jesse',
					lastName: 'Yeary',
					email: 'jesseyeary@gmail.com',
					active: true,
				},
				utcCreateDate: '2023-01-13T22:51:40.000Z',
				utcValidDate: '2023-01-13',
				reason: 'Testing',
				firstName: 'Jesse',
				lastName: 'Yeary',
				email: 'jyeary@sentrilock.com',
				mobilePhoneNumber: '5136875708',
				deviceLocation: 'Front Door ',
				propertyName: "Jesse's House",
				address: {
					id: '1060',
					address1: '8476 Deerway Dr',
					locality: 'Cincinnati',
					stateProvinceRegion: 'OH',
					postalCode: '45236',
					countryCode: 'US',
					geoCoordinates: {
						latitude: 39.2118408,
						longitude: -84.3825828,
					},
				},
			},
		};
		this.loadingService.stop(ApiLoadingKeys.requestAndSaveOneDayCode);
		return resp;
	}

	async getFlexCode(
		id: string,
		queryParams?: QueryParam[]
	): Promise<FlexCodeListResponse[]> {
		this.loadingService.start(ApiLoadingKeys.getFlexCode);
		const params = this.generateHttpParams(queryParams);
		const resp: FlexCodeListResponse[] | PromiseLike<FlexCodeListResponse[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.getFlexCode);
		return resp;
	}

	async requestAndSaveFlexCode(
		id: string,
		flexCodeGen4: FlexCodeGen4,
		notifyByEmail: boolean,
		includeInEmail: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		},
		notifyBySms: boolean,
		includeInSms: {
			address: boolean;
			propertyName: boolean;
			deviceLocation: boolean;
		}
	): Promise<FlexCodeGen4> {
		this.loadingService.start(ApiLoadingKeys.requestAndSaveFlexCode);
		const resp = {
			id: '12',
			deviceId: '1426',
			device: {
				id: '1426',
				serialNumber: '2089396',
				decommissioned: false,
			},
			flexCode: '7413431888029',
			creatorId: '215894751875043328',
			creatorCompanyId: '1234',
			creator: {
				id: '215894751875043328',
				firstName: 'Jesse',
				lastName: 'Yeary',
				email: 'jesseyeary@gmail.com',
				active: true,
			},
			utcCreateDate: '2023-01-13T22:56:02.000Z',
			utcStartTime: '2023-01-13',
			utcEndTime: '2023-01-17',
			reason: 'Testing',
			firstName: 'Jesse',
			lastName: 'Yeary',
			email: 'jyeary@sentrilock.com',
			mobilePhoneNumber: '5136875708',
			locked: true,
			deviceLocation: 'front door',
			propertyName: 'at Home',
			address: {
				id: '1075',
				address1: '9570 Fields Ertel Rd',
				locality: 'Loveland',
				stateProvinceRegion: 'Ohio',
				postalCode: '45040',
				countryCode: 'US',
				geoCoordinates: {
					latitude: 39.2907863,
					longitude: -84.2948411,
				},
			},
			userLocalStartDate: '2023-01-13',
			userLocalEndDate: '2023-01-16',
			userLocalStartTime: '16:00:00',
			userLocalEndTime: '23:30:00',
		};
		this.loadingService.stop(ApiLoadingKeys.requestAndSaveFlexCode);
		return resp;
	}

	async requestDeviceRemovalAction(
		deviceId: string,
		action: DeviceRemovalActions
	): Promise<any> {
		this.loadingService.start(ApiLoadingKeys.requestDeviceRemovalAction);
		const resp = {};
		this.loadingService.stop(ApiLoadingKeys.requestDeviceRemovalAction);
		return resp;
	}

	async transferDeviceAccess(
		sourceDeviceId: string,
		targetDeviceId: string,
		haspFirstName?: string,
		haspLastName?: string,
		haspEmail?: string
	): Promise<{
		success: boolean;
		sourceDeviceHaspCode?: string;
		sourceDeviceType?: string;
		targetDeviceHaspCode?: string;
		targetDeviceType?: string;
	}> {
		const resp = {
			success: true,
			sourceDeviceHaspCode: 'MOCK-SOURCE-HASP-123',
			sourceDeviceType: 'Lock',
			targetDeviceHaspCode: 'MOCK-TARGET-HASP-456',
			targetDeviceType: 'Lock',
		};
		return resp;
	}

	async getDeviceEventHistory(
		id: string,
		queryParams?: QueryParam[]
	): Promise<EventHistory[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceEventHistory);
		const params = this.generateHttpParams(queryParams);
		const resp: EventHistory[] | PromiseLike<EventHistory[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getDeviceEventHistory);
		return resp;
	}

	async getEventHistoryReport(
		id: string,
		type: 'device' | 'user' | 'properties',
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getEventHistoryReport);
		const params = this.generateHttpParams(queryParams);
	}

	async registerNewUser(
		users: User[],
		parentEntityId: string,
		roleId?: string,
		roleName?: string,
		systemCapabilityItems?: SystemCapabilityItem[]
	): Promise<NewUserResponse> {
		this.loadingService.start(ApiLoadingKeys.registerNewUser);
		const systemCapabilities = systemCapabilityItems
			?.filter((it) => it.granted === true)
			.map((it) => it.systemCapability.id);

		const body = {
			users,
			parentEntityId,
			roleId,
			roleName,
			systemCapabilities,
		};

		const resp = {
			data: {
				userIds: ['1224'],
			},
		};
		this.loadingService.stop(ApiLoadingKeys.registerNewUser);
		return resp;
	}

	async checkNewUserRegistrationToken(token: string): Promise<TokenValidity> {
		this.loadingService.start(ApiLoadingKeys.checkNewUserRegistrationToken);
		const resp = {
			tokenRecord: {
				userId: '123',
				email: 'jyeary@sentrilock.com',
				token: '12345',
			},
			valid: true,
		};
		this.loadingService.stop(ApiLoadingKeys.checkNewUserRegistrationToken);
		return resp;
	}

	async saveMobilePhoneNumber(
		userId: string,
		phoneNumber: PhoneNumber
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.saveMobilePhoneNumber);

		this.loadingService.stop(ApiLoadingKeys.saveMobilePhoneNumber);
	}

	async saveEmail(userId: string, newEmail: string): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.saveEmail);

		this.loadingService.stop(ApiLoadingKeys.saveEmail);
	}

	async getSecurityQuestions(
		countryCode: string | undefined
	): Promise<string[]> {
		this.loadingService.start(ApiLoadingKeys.getSecurityQuestions);
		const resp: string[] | PromiseLike<string[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getSecurityQuestions);
		return resp;
	}

	async getSecurityAnswers(userId: string): Promise<UserSecurityAnswer[]> {
		this.loadingService.start(ApiLoadingKeys.getSecurityAnswers);
		const resp: UserSecurityAnswer[] | PromiseLike<UserSecurityAnswer[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getSecurityAnswers);
		return resp;
	}

	async requestPhoneVerificationCode(
		userId: string,
		phoneNumber: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.requestPhoneVerificationCode);

		this.loadingService.stop(ApiLoadingKeys.requestPhoneVerificationCode);
	}

	async verifyPhoneVerificationCode(
		userId: string,
		verificationCode: NumericToken,
		phoneNumber: string
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.verifyPhoneVerificationCode);
		const resp = true;
		this.loadingService.stop(ApiLoadingKeys.verifyPhoneVerificationCode);
		return resp;
	}

	async completeNewUserRegistration(
		password: string,
		token: string,
		userId: string,
		firstName: string,
		lastName: string,
		securityQuestion: string,
		securityAnswer: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.completeNewUserRegistration);
		// Complete the user registration flow by setting their password via the token
		const url = `${this.env.apiUrl}/api/v1/user/${userId}/registration/submit`;

		this.loadingService.stop(ApiLoadingKeys.completeNewUserRegistration);
	}

	async updateDeviceSettings(
		deviceId: string,
		keyDetection: boolean
	): Promise<DeviceSettings | undefined> {
		this.loadingService.start(ApiLoadingKeys.updateDeviceSettings);
		// Update the device settings
		const url = `${this.env.apiUrl}/api/v1/device/${deviceId}/deviceSettings`;
		const deviceSettings = { deviceId: '1', keyDetection: true };
		this.loadingService.stop(ApiLoadingKeys.updateDeviceSettings);
		return deviceSettings;
	}

	async editDeviceControl(
		deviceControlId: string,
		control: DeviceControl
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editDeviceControl);

		this.loadingService.stop(ApiLoadingKeys.editDeviceControl);
	}

	async getCodeGenReportFilterOptions(): Promise<CodeGenReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getCodeGenReportFilterOptions);
		const resp = {
			devices: [],
			ownerCompanies: [],
			generatedByUsers: [],
			assignedToUsers: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getCodeGenReportFilterOptions);
		return resp;
	}

	async generateCodeGenReport(
		queryParams?: QueryParam[]
	): Promise<CodeGenReport[]> {
		this.loadingService.start(ApiLoadingKeys.generateCodeGenReport);
		const params = this.generateHttpParams(queryParams);

		const resp: CodeGenReport[] | PromiseLike<CodeGenReport[]> = [];
		this.loadingService.stop(ApiLoadingKeys.generateCodeGenReport);
		return resp;
	}

	async generateCodeGenReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateCodeGenReportCsv);
		const params = this.generateHttpParams(queryParams);
	}

	async getAccessReportFilterOptions(): Promise<AccessReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getAccessReportFilterOptions);
		const resp = {
			devices: [],
			ownerCompanies: [],
			visitingCompanies: [],
			visitingUsers: [],
			properties: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getAccessReportFilterOptions);
		return resp;
	}

	async generateAccessReport(
		queryParams?: QueryParam[]
	): Promise<AccessReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAccessReport);
		const params = this.generateHttpParams(queryParams);
		const resp: AccessReportRecord[] | PromiseLike<AccessReportRecord[]> = [];
		this.loadingService.stop(ApiLoadingKeys.generateAccessReport);
		return resp;
	}

	async startAccessReportExport(
		queryParams?: QueryParam[]
	): Promise<AccessReportExportStart> {
		return { started: true, export: { status: 'preparing' } };
	}

	async getAccessReportExport(): Promise<AccessReportExportStatus> {
		return { status: 'none' };
	}

	async downloadAccessReportExport(): Promise<void> {}

	async getKeyDetectionReportFilterOptions(): Promise<KeyDetectionReportFilterOptions> {
		this.loadingService.start(
			ApiLoadingKeys.getKeyDetectionReportFilterOptions
		);
		const resp = {
			devices: [],
			ownerCompanies: [],
			visitingCompanies: [],
			visitingUsers: [],
			properties: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getKeyDetectionReportFilterOptions);
		return resp;
	}

	async generateKeyDetectionReport(
		queryParams?: QueryParam[]
	): Promise<KeyDetectionReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateKeyDetectionReport);
		const params = this.generateHttpParams(queryParams);
		const resp:
			KeyDetectionReportRecord[] | PromiseLike<KeyDetectionReportRecord[]> = [];
		this.loadingService.stop(ApiLoadingKeys.generateKeyDetectionReport);
		return resp;
	}

	async generateKeyDetectionReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateKeyDetectionReportCsv);
		const params = this.generateHttpParams(queryParams);
		this.loadingService.stop(ApiLoadingKeys.generateKeyDetectionReportCsv);
	}

	async generateUsageReport(
		queryParams?: QueryParam[]
	): Promise<UsageReport[]> {
		this.loadingService.start(ApiLoadingKeys.generateUsageReport);
		const params = this.generateHttpParams(queryParams);
		const resp: UsageReport[] | PromiseLike<UsageReport[]> = [];
		this.loadingService.stop(ApiLoadingKeys.generateUsageReport);
		return resp;
	}

	async generateUsageReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateUsageReportCsv);
		const params = this.generateHttpParams(queryParams);
	}

	async getInventoryReportFilterOptions(): Promise<InventoryReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getAccessReportFilterOptions);
		const resp = {
			devices: [],
			properties: [],
			statuses: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getAccessReportFilterOptions);
		return resp;
	}

	async generateInventoryReport(
		queryParams?: QueryParam[]
	): Promise<InventoryReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAccessReport);
		const params = this.generateHttpParams(queryParams);
		const resp: InventoryReportRecord[] | PromiseLike<InventoryReportRecord[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.generateAccessReport);
		return resp;
	}

	async generateInventoryReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<InventoryReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAccessReportCsv);
		const params = this.generateHttpParams(queryParams);
		const resp: InventoryReportRecord[] | PromiseLike<InventoryReportRecord[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.generateAccessReportCsv);
		return resp;
	}

	async getPropertyReportFilterOptions(): Promise<PropertyReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getPropertyReportFilterOptions);
		const resp = {
			devices: [],
			deviceStatuses: [],
			propertyNames: [],
			postalCodes: [],
			removalRequested: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getPropertyReportFilterOptions);
		return resp;
	}

	async generatePropertyReport(
		queryParams?: QueryParam[]
	): Promise<PropertyReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generatePropertyReport);
		const params = this.generateHttpParams(queryParams);
		const resp: PropertyReportRecord[] | PromiseLike<PropertyReportRecord[]> =
			[];
		this.loadingService.stop(ApiLoadingKeys.generatePropertyReport);
		return resp;
	}

	async generatePropertyReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generatePropertyReportCsv);
		const params = this.generateHttpParams(queryParams);
	}

	async getDeviceMapData(
		neLat?: number,
		neLng?: number,
		swLat?: number,
		swLng?: number
	): Promise<MapData[]> {
		this.loadingService.start(ApiLoadingKeys.deviceMapHome);
		const resp: MapData[] | PromiseLike<MapData[]> = [];
		this.loadingService.stop(ApiLoadingKeys.deviceMapHome);
		return resp;
	}

	getDashboardSettings(): Promise<DashboardSettings> {
		this.loadingService.start(ApiLoadingKeys.getDashboardSettings);
		const resp = Promise.resolve({
			mapVisibleDeviceCount: '20',
			maxHoursVisitConsideredActive: '8',
		});
		this.loadingService.stop(ApiLoadingKeys.getDashboardSettings);
		return resp;
	}

	async getDeviceAlerts(queryParams?: QueryParam[]): Promise<DeviceAlert[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceAlerts);
		const params = this.generateHttpParams(queryParams);
		const resp: DeviceAlert[] | PromiseLike<DeviceAlert[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getDeviceAlerts);
		return resp;
	}

	async getTotalDeviceAlertsType(): Promise<DeviceAlertTotal[]> {
		this.loadingService.start(ApiLoadingKeys.getTotalDeviceAlertsType);
		const params = this.generateHttpParams();
		const resp: DeviceAlertTotal[] | PromiseLike<DeviceAlertTotal[]> = [];
		this.loadingService.stop(ApiLoadingKeys.getTotalDeviceAlertsType);
		return resp;
	}

	async dismissAlert(
		deviceAlertId: string,
		deviceAlert: DeviceAlert
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.dismissAlert);
		const body = { deviceAlert };
		const resp = true;
		this.loadingService.stop(ApiLoadingKeys.dismissAlert);
		return resp;
	}

	async dismissNote(_deviceNote: DeviceNote): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.dismissNote);
		const resp = true;
		this.loadingService.stop(ApiLoadingKeys.dismissNote);
		return resp;
	}

	async getTimezone(): Promise<Timezone[]> {
		this.loadingService.start(ApiLoadingKeys.getTimezone);
		const resp = [
			{
				timezone: 'EST',
				description: 'Eastern Standard Time',
				name: 'US/Eastern',
			},
			{
				timezone: 'PST',
				description: 'Pacific Standard Time',
				name: 'US/Pacific',
			},
			{
				timezone: 'MST',
				description: 'Mountain Standard Time',
				name: 'US/Mountain',
			},
		];

		this.loadingService.stop(ApiLoadingKeys.getTimezone);
		return resp;
	}

	async getCountriesAvailableForSms(): Promise<CountryAvailableSms> {
		this.loadingService.start(ApiLoadingKeys.getCountryAvailableSms);
		const resp = {
			countryAvailableSms: [
				'us',
				'ca',
				'gb',
				'de',
				'fr',
				'se',
				'ie',
				'nl',
				'be',
				'dk',
				'at',
				'es',
				'tr',
			],
			activeCompanyCountryCode: 'us',
		};
		this.loadingService.stop(ApiLoadingKeys.getCountryAvailableSms);
		return resp;
	}

	async getFeatureFlagByName(featureFlagName: string): Promise<FeatureFlag> {
		this.loadingService.start(ApiLoadingKeys.getFeatureFlagByName);
		const resp = {
			name: 'keyDetection',
			value: true,
		};

		this.loadingService.stop(ApiLoadingKeys.getFeatureFlagByName);
		return resp;
	}

	async getCompanySettingByName(
		companySettingName: string
	): Promise<CompanySetting> {
		this.loadingService.start(ApiLoadingKeys.getCompanySettingByName);
		const resp = {
			name: 'keyDetection',
			value: 'true',
			id: '1',
			companyId: '1',
			settingName: 'test',
		};

		this.loadingService.stop(ApiLoadingKeys.getCompanySettingByName);
		return resp;
	}

	async allowCodeGeneration(
		deviceId: string,
		allowCodeGenerationBasedOnDeviceControl: boolean,
		allowCodeGenerationBasedOnStatus: boolean
	): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.allowCodeGeneration);
		const resp = true;
		this.loadingService.stop(ApiLoadingKeys.allowCodeGeneration);
		return resp;
	}

	async getAlertReportFilterOptions(): Promise<AlertReportFilterOptions> {
		this.loadingService.start(ApiLoadingKeys.getAlertReportFilterOptions);
		const resp = {
			devices: [],
			ownerCompanies: [],
			generatedByUsers: [],
			assignedToUsers: [],
		};
		this.loadingService.stop(ApiLoadingKeys.getAlertReportFilterOptions);
		return resp;
	}

	async generateAlertReport(
		queryParams?: QueryParam[]
	): Promise<AlertReport[]> {
		this.loadingService.start(ApiLoadingKeys.generateAlertReport);
		const params = this.generateHttpParams(queryParams);
		const resp: AlertReport[] | PromiseLike<AlertReport[]> = [];
		this.loadingService.stop(ApiLoadingKeys.generateAlertReport);
		return resp;
	}

	async generateAlertReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateAlertReportCsv);
		const params = this.generateHttpParams(queryParams);
	}

	async generateSingleDeviceCodeGenReportCsv(
		fileName: string,
		id: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getAccessCode);
		const params = this.generateHttpParams(queryParams);
		this.loadingService.stop(ApiLoadingKeys.getAccessCode);
	}

	async getLearningManual(queryParams?: QueryParam[]): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getLearningDoc);
		const params = this.generateHttpParams(queryParams);
		this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
	}

	async getLearningVideo(queryParams?: QueryParam[]): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getLearningDoc);
		const params = this.generateHttpParams(queryParams);
		this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
	}

	async getLearningImage(queryParams?: QueryParam[]): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getLearningDoc);
		const params = this.generateHttpParams(queryParams);
		this.loadingService.stop(ApiLoadingKeys.getLearningDoc);
	}

	async getWhitelistUserListForCompany(
		queryParams: QueryParam[],
		_companyId?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.getWhitelistUserListForCompany);
		this.loadingService.stop(ApiLoadingKeys.getWhitelistUserListForCompany);
	}

	async updateOrAddNoteFromWhitelist(
		id: string,
		updateRequest: SaveNoteRequestBody,
		_companyId?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateOrAddNoteFromWhitelist);
		this.loadingService.stop(ApiLoadingKeys.updateOrAddNoteFromWhitelist);
	}

	async updateWhitelistStatus(
		id: string,
		updateRequest: SaveStatusRequestBody,
		_companyId?: string
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.updateWhitelistStatus);
		this.loadingService.stop(ApiLoadingKeys.updateWhitelistStatus);
	}

	async getCompanyFilterOptions(queryParams?: QueryParam[]): Promise<string[]> {
		this.loadingService.start(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		this.loadingService.stop(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		return [];
	}

	async getPostalCodeFilterOptions(
		queryParams?: QueryParam[]
	): Promise<string[]> {
		this.loadingService.start(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		this.loadingService.stop(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		return [];
	}

	async getInstallationLocationNameFilterOptions(
		queryParams?: QueryParam[]
	): Promise<string[]> {
		this.loadingService.start(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		this.loadingService.stop(
			ApiLoadingKeys.getInstallationLocationNameFilterOptions
		);
		return [];
	}

	async getDeviceDeviceSettingsKV(
		deviceId: string
	): Promise<DeviceSettingsKV[]> {
		this.loadingService.start(ApiLoadingKeys.getDeviceDeviceSettingsKV);
		this.loadingService.stop(ApiLoadingKeys.getDeviceDeviceSettingsKV);
		return [
			{
				id: '1001',
				deviceId: '1001',
				settingName: 'Handedness',
				value: 'right',
			},
			{
				id: '1002',
				deviceId: '1001',
				settingName: 'AutoRelockDelay',
				value: '5',
			},
		];
	}

	async editControlAssignmentsFull(
		controlAssignmentIds: string[],
		editControlAssignment: Components.Schemas.EditControlAssignment
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.editControlAssignments);
		this.loadingService.stop(ApiLoadingKeys.editControlAssignments);
	}

	async getDeviceControlDescendantCompanies(ids: string[]): Promise<any[]> {
		this.loadingService.start(
			ApiLoadingKeys.getDeviceControlDescendantCompanies
		);
		this.loadingService.stop(
			ApiLoadingKeys.getDeviceControlDescendantCompanies
		);
		return [];
	}

	async verifyUsersCanBeAssignedToDeviceGroup(
		deviceGroupId: string,
		entityRelationshipIds: string[]
	): Promise<any[]> {
		this.loadingService.start(
			ApiLoadingKeys.verifyUsersCanBeAssignedToDeviceGroup
		);
		this.loadingService.stop(
			ApiLoadingKeys.verifyUsersCanBeAssignedToDeviceGroup
		);
		return [];
	}

	async getCompanyOwnsDevices(companyId: string): Promise<boolean> {
		this.loadingService.start(ApiLoadingKeys.getCompanyOwnsDevices);
		this.loadingService.stop(ApiLoadingKeys.getCompanyOwnsDevices);
		return true;
	}

	async generateAuditReport(
		queryParams?: QueryParam[]
	): Promise<AuditReportRecord[]> {
		this.loadingService.start(ApiLoadingKeys.generateAuditReport);
		this.loadingService.stop(ApiLoadingKeys.generateAuditReport);
		return [];
	}

	async generateAuditReportCsv(
		fileName: string,
		queryParams?: QueryParam[]
	): Promise<void> {
		this.loadingService.start(ApiLoadingKeys.generateAuditReportCsv);
		this.loadingService.stop(ApiLoadingKeys.generateAuditReportCsv);
		return;
	}
}

export interface APIError {
	errorStatusCode: number;
	errors: [
		{
			statusCode: number;
			title: string;
			detail: string;
		},
	];
}

export interface QueryParam {
	param: string;
	value: string | number | boolean | string[] | number[];
}

export interface PaginationLinks {
	first: string;
	previous: string;
	next: string;
	last: string;
}

export interface PasswordResetTokenStatus {
	status: string;
}

export enum DeviceRemovalActions {
	perform = 'perform',
	request = 'request',
	cancel = 'cancel',
}

export enum ApiLoadingKeys {
	login = 'LOADING_INDICATOR_KEY_ApiMockServiceS_login',
	requestMfaToken = 'LOADING_INDICATOR_KEY_ApiMockServiceS_requestMfaToken',
	verifyMfaToken = 'LOADING_INDICATOR_KEY_ApiMockServiceS_verifyMfaToken',
	updateCustomClaims = 'LOADING_INDICATOR_KEY_ApiMockServiceS_updateCustomClaims',
	getSentrilockCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getSentrilockCompanyId',
	sendPasswordResetEmail = 'LOADING_INDICATOR_KEY_ApiMockServiceS_sendPasswordResetEmail',
	checkPasswordResetTokenStatus = 'LOADING_INDICATOR_KEY_ApiMockServiceS_checkPasswordResetTokenStatus',
	resetPassword = 'LOADING_INDICATOR_KEY_ApiMockServiceS_resetPassword',
	changePassword = 'LOADING_INDICATOR_KEY_ApiMockServiceS_changePassword',
	getUserProfile = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getUserProfile',
	updateUser = 'LOADING_INDICATOR_KEY_ApiMockServiceS_updateUser',
	getUserNotificationPreferences = 'LOADING_INDICATOR_KEY_APISERVICES_getUserNotificationPreferences',
	updateUserNotificationPreferences = 'LOADING_INDICATOR_KEY_APISERVICES_updateUserNotificationPreferences',
	updateUserProfile = 'LOADING_INDICATOR_KEY_ApiMockServiceS_updateUserProfile',
	validateEmail = 'LOADING_INDICATOR_KEY_ApiMockServiceS_validateEmail',
	requestEmailVerificationCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_requestEmailVerificationCode',
	verifyEmailVerificationCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_verifyEmailVerificationCode',
	getUserDetails = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getUserDetails',
	getUserAndRole = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getUserAndRole',
	getUserEventHistory = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getUserEventHistory',
	getAllUsers = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAllUsers',
	searchCompaniesPublicDirectory = 'LOADING_INDICATOR_KEY_ApiMockServiceS_searchCompaniesPublicDirectory',
	getCompanyById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyById',
	getCompanyByInfo = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyByInfo',
	editCompanyById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_editCompanyById',
	updateCompanyRelationship = 'LOADING_INDICATOR_KEY_ApiMockServiceS_updateCompanyRelationship',
	getCompanyRelationships = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyRelationships',
	getCompanyRelationshipsByCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyRelationshipsByCompanyId',
	createCompanyRelationship = 'LOADING_INDICATOR_KEY_ApiMockServiceS_createCompanyRelationship',
	createNewCompany = 'LOADING_INDICATOR_KEY_ApiMockServiceS_createNewCompany',
	getCompanyRolesByCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyRolesByCompanyId',
	createDeviceGroup = 'LOADING_INDICATOR_KEY_ApiMockServiceS_createDeviceGroup',
	updateDeviceGroupMembership = 'LOADING_INDICATOR_KEY_ApiMockServiceS_updateDeviceGroupMembership',
	getSystemCapabilitiesByRoleId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getSystemCapabilitiesByRoleId',
	getRoleCapabilities = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getRoleCapabilities',
	getMyRoles = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getMyRoles',
	getRoleById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getRoleById',
	updateRole = 'LOADING_INDICATOR_KEY_ApiMockServiceS_updateRole',
	getAssignedRoleUsers = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAssignedRoleUsers',
	createNewRole = 'LOADING_INDICATOR_KEY_ApiMockServiceS_createNewRole',
	assignRoleToUserId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_assignRoleToUserId',
	unassignRoleFromUserId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_unassignRoleFromUserId',
	getCompanyUsersByCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyUsersByCompanyId',
	getMyUsers = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getMyUsers',
	getContracts = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getContracts',
	getSingleContract = 'LOADING_INDICATOR_KEY_APISERVICES_getSingleContract',
	saveContract = 'LOADING_INDICATOR_KEY_ApiMockServiceS_saveContract',
	getCompanyPropertiesByCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanyPropertiesByCompanyId',
	createNewProperty = 'LOADING_INDICATOR_KEY_ApiMockServiceS_createNewProperty',
	getProperties = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getProperties',
	getProperty = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getProperty',
	getPropertyEventHistory = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getPropertyEventHistory',
	editPropertyById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_editPropertyById',
	editDeviceById = 'LOADING_INDICATOR_KEY_APISERVICES_editDeviceById',
	getAllDevices = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAllDevices',
	getDevicesByCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDevicesByCompanyId',
	getDevicesByPropertyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDevicesByPropertyId',
	getDevicesByDeviceGroupId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDevicesByDeviceGroupId',
	getAvailableDevices = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAvailableDevices',
	getDeviceById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceById',
	getWasDeviceOwner = 'LOADING_INDICATOR_KEY_APISERVICES_getWasDeviceOwner',
	getDeviceBySerialNumber = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceBySerialNumber',
	getDeviceInstallationPhotos = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceInstallationPhotos',
	saveDeviceInstallationPhoto = 'LOADING_INDICATOR_KEY_ApiMockServiceS_saveDeviceInstallationPhoto',
	getAppNotes = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAppNotes',
	getAllDeviceNotes = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAllDeviceNotes',
	addNote = 'LOADING_INDICATOR_KEY_ApiMockServiceS_addNote',
	addDevice = 'LOADING_INDICATOR_KEY_ApiMockServiceS_addDevice',
	getDeviceProvisionError = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceProvisionError',
	getDeviceGroups = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceGroups',
	getDeviceGroupsByCompanyId = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceGroupsByCompanyId',
	getDeviceGroupOptionss = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceGroupOptions',
	getDeviceGroupById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceGroupById',
	getInheritedDeviceGroupControlById = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getInheritedDeviceGroupControlById',
	getDeviceControls = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceControls',
	transferDeviceOwnership = 'LOADING_INDICATOR_KEY_ApiMockServiceS_transferDeviceOwnership',
	getControlAssignments = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getControlAssignments',
	deleteControlAssignments = 'LOADING_INDICATOR_KEY_ApiMockServiceS_deleteControlAssignments',
	editControlAssignments = 'LOADING_INDICATOR_KEY_ApiMockServiceS_editControlAssignments',
	getControlAssignmentOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getControlAssignmentOptions',
	getOneDayCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getOneDayCode',
	getAccessCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAccessCode',
	requestAndSaveOneDayCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_requestAndSaveOneDayCode',
	getFlexCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getFlexCode',
	requestAndSaveFlexCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_requestAndSaveFlexCode',
	requestDeviceRemovalAction = 'LOADING_INDICATOR_KEY_ApiMockServiceS_requestDeviceRemovalAction',
	getDeviceEventHistory = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceEventHistory',
	getEventHistoryReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getEventHistoryReport',
	registerNewUser = 'LOADING_INDICATOR_KEY_ApiMockServiceS_registerNewUser',
	checkNewUserRegistrationToken = 'LOADING_INDICATOR_KEY_ApiMockServiceS_checkNewUserRegistrationToken',
	saveMobilePhoneNumber = 'LOADING_INDICATOR_KEY_ApiMockServiceS_saveMobilePhoneNumber',
	saveEmail = 'LOADING_INDICATOR_KEY_ApiMockServiceS_saveEmail',
	getSecurityQuestions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getSecurityQuestions',
	getSecurityAnswers = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getSecurityAnswers',
	requestPhoneVerificationCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_requestPhoneVerificationCode',
	verifyPhoneVerificationCode = 'LOADING_INDICATOR_KEY_ApiMockServiceS_verifyPhoneVerificationCode',
	completeNewUserRegistration = 'LOADING_INDICATOR_KEY_ApiMockServiceS_completeNewUserRegistration',
	updateDeviceSettings = 'LOADING_INDICATOR_KEY_APISERVICES_updateDeviceSettings',
	editDeviceControl = 'LOADING_INDICATOR_KEY_ApiMockServiceS_editDeviceControl',
	createDeviceControlsAndControlAssignments = 'LOADING_INDICATOR_KEY_APISERVICES_createDeviceControlsAndControlAssignments',
	getCodeGenReportFilterOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCodeGenReportFilterOptions',
	generateCodeGenReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateCodeGenReport',
	generateCodeGenReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateCodeGenReportCsv',
	getAccessReportFilterOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAccessReportFilterOptions',
	generateAccessReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateAccessReport',
	generateAccessReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateAccessReportCsv',
	getKeyDetectionReportFilterOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getKeyDetectionReportFilterOptions',
	generateKeyDetectionReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateKeyDetectionReport',
	generateKeyDetectionReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateKeyDetectionReportCsv',
	getInventoryReportFilterOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getInventoryReportFilterOptions',
	generateInventoryReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateInventoryReport',
	generateInventoryReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateInventoryReportCsv',
	getPropertyReportFilterOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getPropertyReportFilterOptions',
	generatePropertyReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generatePropertyReport',
	generatePropertyReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generatePropertyReportCsv',
	resendUserRegistration = 'LOADING_INDICATOR_KEY_ApiMockServiceS_resendUserRegistration',
	deviceMapHome = 'LOADING_INDICATOR_KEY_ApiMockServiceS_deviceMapHome',
	getDeviceAlerts = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDeviceAlerts',
	getDashboardSettings = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getDashboardSettings',
	getTotalDeviceAlertsType = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getTotalDeviceAlertsType',
	dismissAlert = 'LOADING_INDICATOR_KEY_ApiMockServiceS_dismissAlert',
	dismissNote = 'LOADING_INDICATOR_KEY_ApiMockServiceS_dismissNote',
	generateUsageReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateUsageReport',
	generateUsageReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateUsageReportCsv',
	getTimezone = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getTimezone',
	getCountryAvailableSms = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCountryAvailableSms',
	getFeatureFlagByName = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getFeatureFlagByName',
	allowCodeGeneration = 'LOADING_INDICATOR_KEY_APISERVICES_allowCodeGeneration',
	getCompanySettingByName = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getCompanySettingByName',
	getAlertReportFilterOptions = 'LOADING_INDICATOR_KEY_ApiMockServiceS_getAlertReportFilterOptions',
	generateAlertReport = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateAlertReport',
	generateAlertReportCsv = 'LOADING_INDICATOR_KEY_ApiMockServiceS_generateAlertReportCsv',
	getLearningDoc = 'LOADING_INDICATOR_KEY_APISERVICES_getLearningDoc',
	getWhitelistUserListForCompany = 'LOADING_INDICATOR_KEY_APISERVICES_getWhitelistUserListForCompany',
	updateOrAddNoteFromWhitelist = 'LOADING_INDICATOR_KEY_APISERVICES_updateOrAddNoteFromWhitelist',
	updateWhitelistStatus = 'LOADING_INDICATOR_KEY_APISERVICES_updateWhitelistStatus',
	getInstallationLocationNameFilterOptions = 'LOADING_INDICATOR_KEY_APISERVICES_getInstallationLocationNameFilterOptions',
	getDeviceDeviceSettingsKV = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceDeviceSettingsKV',
	getDeviceControlDescendantCompanies = 'LOADING_INDICATOR_KEY_APISERVICES_getDeviceControlDescendantCompanies',
	verifyUsersCanBeAssignedToDeviceGroup = 'LOADING_INDICATOR_KEY_APISERVICES_verifyUsersCanBeAssignedToDeviceGroup',
	getCompanyOwnsDevices = 'LOADING_INDICATOR_KEY_APISERVICES_getCompanyOwnsDevices',
	generateAuditReport = 'LOADING_INDICATOR_KEY_APISERVICES_generateAuditReport',
	generateAuditReportCsv = 'LOADING_INDICATOR_KEY_APISERVICES_generateAuditReportCsv',
	getClients = 'LOADING_INDICATOR_KEY_APISERVICES_getClients',
	getClientProperty = 'LOADING_INDICATOR_KEY_APISERVICES_getClientProperty',
	getClientDevices = 'LOADING_INDICATOR_KEY_APISERVICES_getClientDevices',
	getContacts = 'LOADING_INDICATOR_KEY_APISERVICES_getContacts',
	getVerificationOfTaxIdBasedOnValue = 'LOADING_INDICATOR_KEY_APISERVICES_getVerificationOfTaxIdBasedOnValue',
}
